import type { DashboardLayoutType } from '@/api/dashboardsApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PanelSkeleton } from '@/components/ui/skeletons'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { kbdSubmitProps } from '@/hooks/formKeyboardNav'
import { cn, handleCopyToClipboard } from '@/lib/utils'
import { Copy, Download, Info, Lock, Maximize2, Minimize2, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { Children, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type FailedColumn, failedColumnsLabel, resolvePanelErrorMessage } from '../panels/common/failedColumns'

// Delay before a hovered panel becomes interactable.
const PANEL_UNLOCK_DELAY_MS = 700

export interface PanelContainerProps {
	// Panel identification & metadata
	panelId: string
	panelType?: string
	title?: string
	dashboardType?: DashboardLayoutType

	// Content
	children: ReactNode

	// States
	loading?: boolean
	error?: boolean
	errorMessage?: string
	/** HTTP status of the failed request — 403 renders the access-denied state instead of the generic error */
	errorStatus?: number
	noData?: boolean

	// Actions
	editable?: boolean
	onEdit?: (panelId: string) => void
	onClone?: (panelId: string) => void
	onDelete?: (panelId: string) => void
	onRefresh?: (panelId: string) => void
	onDownload?: (panelId: string) => void

	// Full page support
	fullPage?: boolean
	onToggleFullPage?: (panelId: string) => void

	// Styling & layout
	className?: string
	headerClassName?: string
	contentClassName?: string

	// Custom actions (for panel-specific actions)
	customActions?: Array<{
		icon: ReactNode
		tooltip: string
		onClick: () => void
	}>

	// Download configuration
	downloadConfig?: {
		enabled: boolean
		tooltip?: string
	}

	// Disable the hover-to-interact overlay so the panel is interactable
	// immediately (used by preview contexts like add/edit panel).
	disableInteractionOverlay?: boolean

	// Tooltip content for the info icon in the header — provided by panel definitions via getTooltipContent
	infoContent?: ReactNode

	// Partial field access warning state, renders a warning banner above infoContent in the popover
	hasPartialAccess?: boolean
	partialAccessMessage?: string
	// Panel-specific data-quality warning (e.g. LocateDevices/TrackDevices invalid coordinates).
	// Colors the info icon like hasPartialAccess but renders no banner, since the detail already
	// lives in infoContent; avoids showing the same message twice.
	hasInfoWarning?: boolean
	// Overrides the info popover's default width (w-72). Set by panel definitions whose
	// infoContent needs more room, e.g. LocateDevices/TrackDevices' invalid-coordinates table.
	infoPopoverClassName?: string

	// Columns that failed to load (e.g. a configured column no longer exists on the stream) while
	// others still rendered — shown as a red info-icon warning listing each failed column and its error.
	failedColumns?: FailedColumn[]

	// Element-registration callback. The dashboard runs one scroll listener on
	// the grid's scroll container and computes visibility for all panels via
	// getBoundingClientRect; each panel reports its root DOM node here so the
	// dashboard can include it in that check.
	onElementChange?: (panelId: string, element: HTMLDivElement | null) => void
}

/**
 * PanelContainer - Reusable wrapper for all dashboard panels
 *
 * Provides:
 * - Panel header with title
 * - Action buttons (edit, clone, delete, download, etc.)
 * - Loading and error states
 * - No data state
 * - Responsive desktop/mobile views
 * - Full page support
 */
export function PanelContainer({
	panelId,
	panelType,
	title = 'Untitled',
	children,
	loading = false,
	error = false,
	errorMessage,
	errorStatus,
	noData = false,
	editable = false,
	onEdit,
	onClone,
	onDelete,
	onRefresh,
	onDownload,
	fullPage = false,
	onToggleFullPage,
	className,
	headerClassName,
	contentClassName,
	customActions = [],
	downloadConfig,
	disableInteractionOverlay = false,
	infoContent,
	hasPartialAccess = false,
	partialAccessMessage,
	hasInfoWarning = false,
	infoPopoverClassName,
	failedColumns,
	onElementChange,
	dashboardType,
}: PanelContainerProps) {
	const hasFailedColumns = (failedColumns?.length ?? 0) > 0
	// Callback ref forwards the panel's root element to the dashboard so a single
	// scroll listener can compute visibility for all panels in one pass.
	const setRootElement = useCallback(
		(el: HTMLDivElement | null) => {
			onElementChange?.(panelId, el)
		},
		[onElementChange, panelId],
	)

	const [infoDialogOpen, setInfoDialogOpen] = useState(false)
	const [infoPopoverOpen, setInfoPopoverOpen] = useState(false)
	const infoContentRef = useRef<HTMLDivElement>(null)
	const infoCloseTimerRef = useRef<number | null>(null)
	const resolvedInfoContent = infoContent ?? <p>No additional information is available for this panel.</p>

	const openInfoPopover = useCallback(() => {
		if (infoCloseTimerRef.current) {
			clearTimeout(infoCloseTimerRef.current)
			infoCloseTimerRef.current = null
		}
		setInfoPopoverOpen(true)
	}, [])

	const closeInfoPopover = useCallback(() => {
		infoCloseTimerRef.current = window.setTimeout(() => setInfoPopoverOpen(false), 100)
	}, [])

	const handleCopyInfo = useCallback(() => {
		handleCopyToClipboard(infoContentRef.current?.innerText?.trim() ?? '', {
			successMessage: 'Panel info copied to clipboard',
		})
	}, [])

	// Track interactive state for hover-to-interact behavior on content area.
	// Panel starts locked; after the cursor lingers for PANEL_UNLOCK_DELAY_MS the
	// panel becomes interactive. Leaving the panel cancels the pending timer and
	// re-locks. While locked, only the wheel is gated — see the capture listener below.
	const [isInteractive, setIsInteractive] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)
	const unlockTimerRef = useRef<number | null>(null)
	const pointerInsideRef = useRef(false)
	const deferredRelockRef = useRef<(() => void) | null>(null)

	// Show centered loader only when loading AND no real content to display.
	// Children.toArray strips falsy values, so gated JSX like `{cond && <X/>}` counts as empty.
	// On auto-refresh/save reload, parent re-renders children with stale data → loader stays hidden.
	const hasRenderableChildren = useMemo(() => Children.toArray(children).length > 0, [children])
	const isInitialLoad = loading && !hasRenderableChildren

	// With nothing left to draw, the body reports the failure itself (see renderContent), so the header
	// stops repeating it — no red info icon, no banner in the popover. While rows survive, the header is
	// the only place the failure can go.
	const showColumnFailure = hasFailedColumns && !(error && !hasRenderableChildren)

	const failedColumnsBanner = showColumnFailure && (
		<div className='p-2 rounded bg-destructive/10 text-destructive border border-destructive/20 text-body font-medium flex items-start gap-1.5'>
			<Info className='size-(--icon-standalone) text-destructive shrink-0 mt-0.5' />
			<div>
				<p>{failedColumnsLabel(failedColumns ?? [])}</p>
				<ul className='list-disc pl-4 mt-1 space-y-0.5'>
					{failedColumns?.map(col => (
						<li key={`${col.name}-${col.index}`}>
							<span className='font-semibold'>{col.name}</span>: {col.error}
						</li>
					))}
				</ul>
			</div>
		</div>
	)

	const clearUnlockTimer = useCallback(() => {
		if (unlockTimerRef.current !== null) {
			clearTimeout(unlockTimerRef.current)
			unlockTimerRef.current = null
		}
	}, [])

	// Clear hover-close timer on unmount to prevent setState on unmounted component
	useEffect(() => {
		return () => {
			if (infoCloseTimerRef.current !== null) {
				clearTimeout(infoCloseTimerRef.current)
			}
		}
	}, [])

	const clearDeferredRelock = useCallback(() => {
		if (deferredRelockRef.current !== null) {
			window.removeEventListener('pointerup', deferredRelockRef.current)
			window.removeEventListener('pointercancel', deferredRelockRef.current)
			deferredRelockRef.current = null
		}
	}, [])

	// Clear unlock timer and any pending gesture-end listener on unmount
	useEffect(() => {
		return () => {
			clearUnlockTimer()
			clearDeferredRelock()
		}
	}, [clearUnlockTimer, clearDeferredRelock])

	// While locked, keep the wheel from reaching panel content so scrolling past a chart does
	// not zoom it. stopPropagation only — no preventDefault, so the page still scrolls.
	useEffect(() => {
		const content = contentRef.current
		if (!content || disableInteractionOverlay || isInteractive) {
			return
		}
		const swallowWheel = (e: WheelEvent) => e.stopPropagation()
		content.addEventListener('wheel', swallowWheel, { capture: true, passive: true })
		return () => content.removeEventListener('wheel', swallowWheel, { capture: true })
	}, [disableInteractionOverlay, isInteractive])

	const handlePanelMouseEnter = useCallback(
		(e: React.MouseEvent) => {
			pointerInsideRef.current = true
			clearDeferredRelock()
			if (disableInteractionOverlay || isInteractive) {
				return
			}
			clearUnlockTimer()
			// Re-entering mid-drag: skip the dwell, the gesture already started here.
			if (e.buttons !== 0) {
				setIsInteractive(true)
				return
			}
			unlockTimerRef.current = window.setTimeout(() => {
				setIsInteractive(true)
				unlockTimerRef.current = null
			}, PANEL_UNLOCK_DELAY_MS)
		},
		[disableInteractionOverlay, isInteractive, clearUnlockTimer, clearDeferredRelock],
	)

	const handlePanelMouseLeave = useCallback(
		(e: React.MouseEvent) => {
			pointerInsideRef.current = false
			clearUnlockTimer()

			// A drag crossing the panel edge must not re-lock — the border would flash grey
			// mid-gesture. Defer to gesture end.
			if (e.buttons !== 0) {
				clearDeferredRelock()
				const relockOnGestureEnd = () => {
					clearDeferredRelock()
					if (!pointerInsideRef.current) {
						setIsInteractive(false)
					}
				}
				deferredRelockRef.current = relockOnGestureEnd
				window.addEventListener('pointerup', relockOnGestureEnd)
				window.addEventListener('pointercancel', relockOnGestureEnd)
				return
			}

			clearDeferredRelock()
			setIsInteractive(false)
		},
		[clearUnlockTimer, clearDeferredRelock],
	)

	// Determine if we should show action buttons
	const hasActions = editable && (onEdit || onClone || onDelete || onDownload || customActions.length > 0)

	const handleEdit = (e: React.MouseEvent) => {
		e.stopPropagation()
		onEdit?.(panelId) // prevent event bubbling
	}

	const handleClone = (e: React.MouseEvent) => {
		e.stopPropagation()
		onClone?.(panelId)
	}

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation()
		onDelete?.(panelId)
	}

	const handleRefresh = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		onRefresh?.(panelId)
	}

	const handleDownload = (e: React.MouseEvent) => {
		e.stopPropagation()
		onDownload?.(panelId)
	}

	const handleToggleFullPage = (e: React.MouseEvent) => {
		e.stopPropagation()
		onToggleFullPage?.(panelId)
	}

	// Render panel content based on state
	const renderContent = () => {
		// Access denied (403): the role has no access to the panel's stream.
		// Show the backend message with a lock icon and no retry — retrying
		// cannot succeed until the role's stream permissions change.
		// hasRenderableChildren (via Children.toArray) is used instead of !children so
		// that parents passing multiple gated expressions like {cond && <X/>}{cond && <Y/>}
		// — which form a truthy [false, false] array — are still treated as "no content".
		if (error && errorStatus === 403 && !hasRenderableChildren) {
			const displayMessage =
				errorMessage && errorMessage.toLowerCase() !== 'forbidden'
					? errorMessage
					: panelType === 'sessions'
						? 'Your role does not have access to session rules, which this panel requires.'
						: 'You do not have access to the data in this panel.'
			return (
				<div className='flex flex-col items-center justify-center h-full gap-2'>
					<Lock className='size-4 text-muted-foreground' aria-hidden='true' />
					<p className='text-body text-muted-foreground text-center max-w-md px-3'>{displayMessage}</p>
				</div>
			)
		}

		// For error state WITHOUT existing data, show what the backend reported so the cause is readable in
		// place. Retrying lives on the header icon — the body stays plain. Clamped so a long message can't
		// push the panel around; the full text stays available on hover.
		if (error && !hasRenderableChildren) {
			const displayMessage = resolvePanelErrorMessage(errorMessage)
			return (
				<div className='flex flex-col items-center justify-center h-full px-3'>
					<p
						className='text-destructive text-body font-medium text-center max-w-md line-clamp-3'
						title={displayMessage}>
						{displayMessage}
					</p>
				</div>
			)
		}

		// For no data state, show no data message in center
		if (noData) {
			return (
				<div className='flex flex-col items-center justify-center h-full gap-2'>
					<p className='text-body text-muted-foreground'>No data</p>
				</div>
			)
		}

		// Initial load (panel has never settled): show a panel-body skeleton.
		// Auto-refresh stays silent — keep stale children visible without any loader.
		if (isInitialLoad) {
			return (
				<PanelSkeleton
					panelType={panelType}
					withHeader={false}
					className='size-full p-0'
					dashboardType={dashboardType}
				/>
			)
		}

		// For refresh (children exist), show children with corner loader/error indicator
		// For error WITH existing data, show children with corner error indicator
		return children
	}

	// Handle keyboard events for accessibility on content area
	const handleContentKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Escape' && isInteractive) {
				e.preventDefault()
				setIsInteractive(false)
			}
		},
		[isInteractive],
	)

	return (
		<>
			<div
				ref={setRootElement}
				className={cn(
					'panel-container-root border rounded-lg relative group h-full flex flex-col transition-colors',
					isInteractive && 'panel-interactive ring-1 ring-primary/30',
					className,
				)}
				onMouseEnter={handlePanelMouseEnter}
				onMouseLeave={handlePanelMouseLeave}>
				{/* Panel Header */}
				<div className={cn('flex items-center justify-between px-3 py-1 rounded-t-lg', editable && headerClassName)}>
					<div className='flex items-center gap-2 flex-1 min-w-0'>
						{/* Panel Title */}
						<h3 className='text-body font-medium truncate flex-1' title={title}>
							{title}
						</h3>

						{/* Retry lives here for every failure — the body carries the message, the header carries the
						    action. Excluded for 403: retrying cannot succeed without a permission change. */}
						{error && errorStatus !== 403 && onRefresh && (
							<button
								type='button'
								aria-label='Retry loading panel data'
								className='flex items-center text-destructive hover:text-destructive/80 transition-colors border-0 bg-transparent p-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-destructive/50 rounded'
								onClick={handleRefresh}
								onMouseDown={e => e.stopPropagation()}>
								<Spinner spinning={false} />
							</button>
						)}
					</div>

					{/* Panel controls remain visible so panel information is always discoverable. */}
					<div className='hidden md:flex items-center gap-1 shrink-0'>
						{/* Full Page Toggle */}
						{onToggleFullPage && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant='ghost'
										size='sm'
										className='size-control p-0'
										data-testid={`panel-full-page-${panelId}`}
										onClick={handleToggleFullPage}>
										{fullPage ? (
											<Minimize2 className='size-(--icon-standalone)' />
										) : (
											<Maximize2 className='size-(--icon-standalone)' />
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>{fullPage ? 'Exit full page' : 'View full page'}</TooltipContent>
							</Tooltip>
						)}

						{/* Edit Button */}
						{hasActions && onEdit && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant='ghost'
										size='sm'
										className='size-control p-0'
										data-testid={`panel-edit-${panelId}`}
										onClick={handleEdit}>
										<Pencil className='size-(--icon-standalone)' />
									</Button>
								</TooltipTrigger>
								<TooltipContent>Edit panel</TooltipContent>
							</Tooltip>
						)}

						{/* Clone Button */}
						{hasActions && onClone && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant='ghost'
										size='sm'
										className='size-control p-0'
										data-testid={`panel-clone-${panelId}`}
										onClick={handleClone}>
										<Copy className='size-(--icon-standalone)' />
									</Button>
								</TooltipTrigger>
								<TooltipContent>Clone panel</TooltipContent>
							</Tooltip>
						)}

						{/* Delete Button */}
						{hasActions && onDelete && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant='ghost'
										size='sm'
										className='size-control p-0'
										data-testid={`panel-delete-${panelId}`}
										onClick={handleDelete}>
										<Trash2 className='size-(--icon-standalone)' />
									</Button>
								</TooltipTrigger>
								<TooltipContent>Delete panel</TooltipContent>
							</Tooltip>
						)}

						{/* Download Button */}
						{downloadConfig?.enabled && onDownload && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant='ghost' size='sm' className='size-control p-0' onClick={handleDownload}>
										<Download className='size-(--icon-standalone)' />
									</Button>
								</TooltipTrigger>
								<TooltipContent>{downloadConfig.tooltip || 'Download panel data'}</TooltipContent>
							</Tooltip>
						)}

						{/* Info icon — content provided by panel definition via getTooltipContent */}
						<Popover open={infoPopoverOpen} onOpenChange={setInfoPopoverOpen}>
							<PopoverTrigger asChild>
								<Button
									variant='ghost'
									size='sm'
									className={cn(
										'size-control p-0',
										showColumnFailure
											? 'text-destructive hover:text-destructive/80'
											: (hasPartialAccess || hasInfoWarning) && 'text-warning hover:text-warning/80',
									)}
									aria-label='Panel information'
									data-testid={`panel-info-${panelId}`}
									onMouseEnter={openInfoPopover}
									onMouseLeave={closeInfoPopover}>
									<Info className='size-(--icon-standalone)' />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								side='bottom'
								align='start'
								className={cn(infoPopoverClassName?.trim() ? infoPopoverClassName : 'w-72', 'p-0 overflow-hidden')}
								onMouseEnter={openInfoPopover}
								onMouseLeave={closeInfoPopover}>
								<div className='flex items-center justify-between px-3 py-1.5 border-b border-border/40'>
									<span className='text-body font-semibold'>Panel Info</span>
									<Button
										variant='ghost'
										size='sm'
										className='h-5 w-5 p-0 shrink-0'
										aria-label='Copy panel info'
										onClick={handleCopyInfo}>
										<Copy className='size-(--icon-standalone)' />
									</Button>
								</div>
								<div ref={infoContentRef} className='text-body px-3 py-2 max-h-64 overflow-y-auto space-y-2'>
									{failedColumnsBanner}
									{hasPartialAccess && (
										<div className='p-2 rounded bg-status-warning-bg text-status-warning-text border border-warning/20 text-body font-medium flex items-start gap-1.5'>
											<Info className='size-(--icon-standalone) text-warning shrink-0 mt-0.5' />
											<span>
												{partialAccessMessage ||
													'You have access to only certain fields, and data is shown based on those fields only.'}
											</span>
										</div>
									)}
									{resolvedInfoContent}
								</div>
							</PopoverContent>
						</Popover>

						{/* Custom Actions */}
						{hasActions &&
							customActions.map(action => (
								<Tooltip key={action.tooltip}>
									<TooltipTrigger asChild>
										<Button
											variant='ghost'
											size='sm'
											className='size-control p-0'
											onClick={e => {
												e.stopPropagation()
												action.onClick()
											}}>
											{action.icon}
										</Button>
									</TooltipTrigger>
									<TooltipContent>{action.tooltip}</TooltipContent>
								</Tooltip>
							))}
					</div>

					{/* Action Buttons - Mobile View (Dropdown) */}
					{
						<div className='md:hidden shrink-0'>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' size='sm' className='size-control p-0'>
										<MoreVertical className='size-(--icon-standalone)' />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end'>
									{onToggleFullPage && (
										<>
											<DropdownMenuItem data-testid={`panel-full-page-${panelId}`} onClick={handleToggleFullPage}>
												{fullPage ? (
													<>
														<Minimize2 className='size-(--icon-standalone) mr-2' />
														Exit Full Page
													</>
												) : (
													<>
														<Maximize2 className='size-(--icon-standalone) mr-2' />
														View Full Page
													</>
												)}
											</DropdownMenuItem>
											<DropdownMenuSeparator />
										</>
									)}

									{onEdit && (
										<DropdownMenuItem data-testid={`panel-edit-${panelId}`} onClick={handleEdit}>
											<Pencil className='size-(--icon-standalone) mr-2' />
											Edit Panel
										</DropdownMenuItem>
									)}

									{onClone && (
										<DropdownMenuItem data-testid={`panel-clone-${panelId}`} onClick={handleClone}>
											<Copy className='size-(--icon-standalone) mr-2' />
											Clone Panel
										</DropdownMenuItem>
									)}

									{onDelete && (
										<DropdownMenuItem
											data-testid={`panel-delete-${panelId}`}
											onClick={handleDelete}
											className='text-destructive'>
											<Trash2 className='size-(--icon-standalone) mr-2' />
											Delete Panel
										</DropdownMenuItem>
									)}

									{downloadConfig?.enabled && onDownload && (
										<>
											<DropdownMenuSeparator />
											<DropdownMenuItem onClick={handleDownload}>
												<Download className='size-(--icon-standalone) mr-2' />
												{downloadConfig.tooltip || 'Download Data'}
											</DropdownMenuItem>
										</>
									)}

									{customActions.length > 0 && (
										<>
											<DropdownMenuSeparator />
											{customActions.map(action => (
												<DropdownMenuItem
													key={action.tooltip}
													onClick={e => {
														e.stopPropagation()
														action.onClick()
													}}>
													<span className='mr-2'>{action.icon}</span>
													{action.tooltip}
												</DropdownMenuItem>
											))}
										</>
									)}

									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem data-testid={`panel-info-${panelId}`} onClick={() => setInfoDialogOpen(true)}>
											<Info className='size-(--icon-standalone) mr-2' />
											Panel Info
										</DropdownMenuItem>
									</>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					}
				</div>

				{/* Panel Content — hover-to-interact: the wheel is gated until the dwell
				    timer lapses, pointer input is not. */}
				<div
					ref={contentRef}
					className={cn('flex-1 min-h-0 px-3 pt-1 pb-3 overflow-hidden relative', contentClassName)}
					onKeyDown={handleContentKeyDown}>
					{renderContent()}
				</div>
			</div>

			<Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
				<DialogContent className='max-w-md'>
					<DialogHeader>
						<div className='flex items-center justify-between'>
							<DialogTitle>{title || 'Panel Info'}</DialogTitle>
							<Button
								variant='ghost'
								size='sm'
								className='size-control p-0 shrink-0'
								aria-label='Copy panel info'
								onClick={handleCopyInfo}>
								<Copy className='size-(--icon-standalone)' />
							</Button>
						</div>
					</DialogHeader>
					<div ref={infoContentRef} className='text-body max-h-96 overflow-y-auto space-y-2'>
						{failedColumnsBanner}
						{resolvedInfoContent}
					</div>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setInfoDialogOpen(false)} {...kbdSubmitProps()}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
